import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import RoleProtectedRoute from './components/RoleProtectedRoute';

import { ComponentLoader } from './components/LazyLoadBoundary';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';

// Critical path - load immediately
import Home from './pages/home';
import Login from './pages/login';
import Register from './pages/register';
import AdminUserManagementSystem from './pages/admin-user-management-system/index';
import AdminUserAccountManagement from './pages/admin-user-account-management/index';
import AdminContentModerationDashboard from './pages/admin-content-moderation-dashboard/index';
import AdminOperationsDashboard from './pages/admin-operations-dashboard/index';
import SecurityMonitoringDashboard from './pages/security-monitoring-dashboard/index';
import SecurityAuditDashboard from '../pages/security-audit-dashboard';
import JobSeekerDashboard from './pages/job-seeker-dashboard';
import DashboardOverview from './pages/company-dashboard-hub/components/DashboardOverview';
import Jobs from './pages/job-seeker-dashboard/components/Jobs';
import SavedJobs from './pages/job-seeker-dashboard/components/SavedJobs';
import Applications from './pages/job-seeker-dashboard/components/Applications';
import CompanyDashboardHub from './pages/company-dashboard-hub';
import MultiCompanyNavigationSidebar from './pages/multi-company-navigation-sidebar/index';
import JobDetails from './pages/job-details/index';
import CompanyProfile from './pages/company-dashboard-hub/components/CompanyProfile';
import AnalyticsDashboard from './pages/analytics-dashboard';
import ErrorMonitoringDashboard from './pages/error-monitoring-dashboard';
import SEOValidationDashboard from './pages/seo-validation-dashboard';
import ResendApiTest from './pages/resend-api-test/index';

// Route-based code splitting - existing implementation
const PublicJobSearchResults = lazy(() => import('./pages/public-job-search-results'));
const CompanyLocationDetail = lazy(() => import('./pages/company-location-detail'));

// Dashboard routes - role-based chunking
const EmployerDashboard = lazy(() => import('./pages/employer-dashboard'));

// Admin routes - separate chunk
const AdminAuthenticationPortal = lazy(() => import('./pages/admin-authentication-portal'));
const AdminSupportManagementSystem = lazy(() => import('./pages/admin-support-management-system'));

// Onboarding flows
const JobSeekerOnboardingWizard = lazy(() => import('./pages/job-seeker-onboarding-wizard'));
const EmployerOnboardingCompanySetup = lazy(() => import('./pages/employer-onboarding-company-setup'));
const DashboardOnboarding = lazy(() => import('./pages/dashboard/onboarding'));

// Job management
const EnhancedJobCreationWizard = lazy(() => import('./pages/enhanced-job-creation-wizard'));
const EmployerJobManagement = lazy(() => import('./pages/employer-job-management'));
const JobSeekerJobs = lazy(() => import('./pages/job-seeker-jobs'));

// Other features
const NotificationsHub = lazy(() => import('./pages/notifications-hub'));
const SupportTicketCreationHub = lazy(() => import('./pages/support-ticket-creation-hub'));
const SupportTicketManagementDashboard = lazy(() => import('./pages/support-ticket-management-dashboard'));
const SubscriptionManagementHub = lazy(() => import('./pages/subscription-management-hub'));
const MultiCompanySubscriptionDashboard = lazy(() => import('./pages/multi-company-subscription-dashboard'));
const CandidateUnlockSystem = lazy(() => import('./pages/candidate-unlock-system'));
const CompanyTeamManagement = lazy(() => import('./pages/company-team-management'));

// Static pages
const AboutUs = lazy(() => import('./pages/about-us'));
const Contact = lazy(() => import('./pages/contact'));
const HelpCenter = lazy(() => import('./pages/help-center'));
const ForEmployersLandingPage = lazy(() => import('./pages/for-employers-landing-page'));
const TermsOfService = lazy(() => import('./pages/terms-of-service'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const CookiePolicy = lazy(() => import('./pages/cookie-policy'));

// Auth pages
const ForgotPassword = lazy(() => import('./pages/forgot-password'));
const ResetPassword = lazy(() => import('./pages/reset-password'));
const VerifyEmail = lazy(() => import('./pages/verify-email'));
const SelectRole = lazy(() => import('./pages/select-role'));
const AuthCallback = lazy(() => import('./pages/auth-callback'));

// Error pages
const NotFound = lazy(() => import('./pages/NotFound'));
const Error404 = lazy(() => import('./pages/404-error-page'));
const Error500 = lazy(() => import('./pages/500-error-page'));

// Design system
const DesignSystemPlayground = lazy(() => import('./pages/design-system-playground'));
const DesignSystemHub = lazy(() => import('./pages/design-system-hub'));

// Privacy & compliance
const GDPRPrivacyControlCenter = lazy(() => import('./pages/gdpr-privacy-control-center'));
const DataProcessingTransparencyPortal = lazy(() => import('./pages/data-processing-transparency-portal'));
const CookieConsentManagementHub = lazy(() => import('./pages/cookie-consent-management-hub'));

// Testing & monitoring
const EndToEndTester = lazy(() => import('./pages/end-to-end-tester'));
const AdminDeploymentChecklistDashboard = lazy(() => import('./pages/admin-deployment-checklist-dashboard'));
const BackupRecoveryDashboard = lazy(() => import('./pages/backup-recovery-dashboard'));
const EmailManagementDashboard = lazy(() => import('./pages/email-management-dashboard'));
const MobileResponsivenessDashboard = lazy(() => import('./components/mobile/MobileResponsivenessDashboard'));

// AI System Testing Dashboard
const AISystemTestingDashboard = lazy(() => import('./pages/ai-system-testing-dashboard'));

// Analytics wrapper component (must be inside Router)
function AnalyticsWrapper({ children }) {
  useGoogleAnalytics();
  return children;
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AnalyticsWrapper>
          <ScrollToTop />
          <Suspense fallback={<ComponentLoader height="100vh" message="Loading page..." />}>
            <RouterRoutes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/select-role" element={<SelectRole />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/for-employers-landing-page" element={<ForEmployersLandingPage />} />

              {/* Error Pages */}
              <Route path="/404-error-page" element={<Error404 />} />
              <Route path="/500-error-page" element={<Error500 />} />

              {/* Support Ticket Routes - Protected */}
              <Route 
                path="/support-ticket-management-dashboard" 
                element={
                  <RoleProtectedRoute requireAuth={true}>
                    <SupportTicketManagementDashboard />
                  </RoleProtectedRoute>
                } 
              />
              
              {/* Admin Support Management - Admin Only */}
              <Route 
                path="/admin-support-management-system" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <AdminSupportManagementSystem />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin User Management - Admin Only */}
              <Route 
                path="/admin-user-management-system" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <AdminUserManagementSystem />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin User Account Management - Admin Only */}
              <Route 
                path="/admin-user-account-management" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <AdminUserAccountManagement />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin Content Moderation - Admin Only */}
              <Route 
                path="/admin-content-moderation-dashboard" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <AdminContentModerationDashboard />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin Operations Dashboard - Admin Only */}
              <Route 
                path="/admin-operations-dashboard" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <AdminOperationsDashboard />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin Security Monitoring - Admin Only */}
              <Route 
                path="/security-monitoring-dashboard" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <SecurityMonitoringDashboard />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin Security Audit Dashboard - Admin Only */}
              <Route 
                path="/security-audit-dashboard" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <SecurityAuditDashboard />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin Backup & Recovery Dashboard - Admin Only */}
              <Route 
                path="/backup-recovery-dashboard" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <BackupRecoveryDashboard />
                  </RoleProtectedRoute>
                } 
              />

              {/* Email Management Dashboard - All authenticated users */}
              <Route 
                path="/email-management-dashboard" 
                element={
                  <RoleProtectedRoute requireAuth={true}>
                    <EmailManagementDashboard />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin Deployment Checklist - Admin Only */}
              <Route 
                path="/admin-deployment-checklist-dashboard" 
                element={
                  <RoleProtectedRoute requireAuth={true} allowedRoles={['admin']}>
                    <AdminDeploymentChecklistDashboard />
                  </RoleProtectedRoute>
                } 
              />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<Suspense fallback={<ComponentLoader />}><AdminAuthenticationPortal /></Suspense>} />
              <Route path="/admin/users" element={<RoleProtectedRoute allowedRoles={['admin']}><AdminUserManagementSystem /></RoleProtectedRoute>} />
              <Route path="/admin/user-accounts" element={<RoleProtectedRoute allowedRoles={['admin']}><AdminUserAccountManagement /></RoleProtectedRoute>} />
              <Route path="/admin/content-moderation" element={<RoleProtectedRoute allowedRoles={['admin']}><AdminContentModerationDashboard /></RoleProtectedRoute>} />
              <Route path="/admin/operations" element={<RoleProtectedRoute allowedRoles={['admin']}><AdminOperationsDashboard /></RoleProtectedRoute>} />
              <Route path="/admin/security" element={<RoleProtectedRoute allowedRoles={['admin']}><SecurityMonitoringDashboard /></RoleProtectedRoute>} />
              <Route path="/admin/support" element={<RoleProtectedRoute allowedRoles={['admin']}><Suspense fallback={<ComponentLoader />}><AdminSupportManagementSystem /></Suspense></RoleProtectedRoute>} />
              <Route path="/admin/deployment" element={<RoleProtectedRoute allowedRoles={['admin']}><Suspense fallback={<ComponentLoader />}><AdminDeploymentChecklistDashboard /></Suspense></RoleProtectedRoute>} />
              <Route path="/admin/backup-recovery" element={<RoleProtectedRoute allowedRoles={['admin']}><Suspense fallback={<ComponentLoader />}><BackupRecoveryDashboard /></Suspense></RoleProtectedRoute>} />
              <Route path="/admin/email-management" element={<RoleProtectedRoute allowedRoles={['admin']}><Suspense fallback={<ComponentLoader />}><EmailManagementDashboard /></Suspense></RoleProtectedRoute>} />
              <Route path="/admin/error-monitoring" element={<RoleProtectedRoute allowedRoles={['admin', 'super_admin']}><ErrorMonitoringDashboard /></RoleProtectedRoute>} />
              <Route path="/admin/analytics" element={<RoleProtectedRoute allowedRoles={['admin', 'super_admin']}><AnalyticsDashboard /></RoleProtectedRoute>} />

              {/* AI System Testing Dashboard */}
              <Route
                path="/ai-system-testing-dashboard"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                    <Suspense fallback={<ComponentLoader />}>
                      <AISystemTestingDashboard />
                    </Suspense>
                  </RoleProtectedRoute>
                }
              />

              {/* Privacy & Legal Pages */}
              <Route path="/gdpr-privacy-control-center" element={<GDPRPrivacyControlCenter />} />
              <Route path="/cookie-consent-management-hub" element={<CookieConsentManagementHub />} />
              <Route path="/data-processing-transparency-portal" element={<DataProcessingTransparencyPortal />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help-center" element={<HelpCenter />} />

              {/* Legal Pages */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help-center" element={<HelpCenter />} />

              {/* Job Seeker Protected Routes */}
              <Route 
                path="/dashboard/onboarding/*" 
                element={
                  <RoleProtectedRoute allowedRoles={['job_seeker']}>
                    <DashboardOnboarding />
                  </RoleProtectedRoute>
                } 
              />
              <Route 
                path="/onboarding/seeker/*" 
                element={
                  <RoleProtectedRoute allowedRoles={['job_seeker']}>
                    <JobSeekerOnboardingWizard />
                  </RoleProtectedRoute>
                } 
              />
              
              {/* Job Seeker Dashboard Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <RoleProtectedRoute allowedRoles={['job_seeker']}>
                    <JobSeekerDashboard />
                  </RoleProtectedRoute>
                }
              >
                <Route index element={<DashboardOverview />} />
                <Route path="jobs" element={<Jobs />} />
                <Route path="saved-jobs" element={<SavedJobs />} />
                <Route path="applications" element={<Applications />} />
              </Route>
              
              {/* Employer Protected Routes */}
              <Route 
                path="/company-dashboard-hub/:companyId/*" 
                element={
                  <RoleProtectedRoute allowedRoles={['hiring_manager', 'company_admin', 'admin']}>
                    <CompanyDashboardHub />
                  </RoleProtectedRoute>
                } 
              />
              
              {/* Company Admin Only Routes */}
              <Route 
                path="/company-team-management" 
                element={
                  <RoleProtectedRoute allowedRoles={['company_admin', 'admin']}>
                    <CompanyTeamManagement />
                  </RoleProtectedRoute>
                } 
              />
              
              <Route 
                path="/multi-company-navigation-sidebar" 
                element={
                  <RoleProtectedRoute allowedRoles={['company_admin', 'admin']}>
                    <MultiCompanyNavigationSidebar />
                  </RoleProtectedRoute>
                } 
              />
              
              {/* Multi-Company Subscription Dashboard */}
              <Route 
                path="/multi-company-subscription-dashboard" 
                element={
                  <RoleProtectedRoute allowedRoles={['hiring_manager', 'company_admin', 'admin']}>
                    <MultiCompanySubscriptionDashboard />
                  </RoleProtectedRoute>
                } 
              />
              
              {/* Public Job Search Routes */}
              <Route path="/jobs" element={<PublicJobSearchResults />} />
              <Route path="/jobs/:id" element={<JobDetails />} />
              
              {/* Company Public Routes */}
              <Route path="/c/:slug" element={<CompanyProfile />} />
              <Route path="/c/:slug/locations/:locationId" element={<CompanyLocationDetail />} />
              
              {/* Notifications Hub - Protected */}
              <Route 
                path="/notifications-hub" 
                element={
                  <RoleProtectedRoute requireAuth={true}>
                    <NotificationsHub />
                  </RoleProtectedRoute>
                } 
              />
              
              {/* Testing Routes */}
              <Route path="/end-to-end-tester" element={<EndToEndTester />} />
              
              {/* Design System Route */}
              <Route path="/design-system-playground" element={<DesignSystemPlayground />} />
              <Route path="/_ds" element={<DesignSystemPlayground />} />
              <Route path="/_ds/*" element={<DesignSystemPlayground />} />
              
              {/* Mobile Responsiveness Testing (Development Only) */}
              <Route 
                path="/mobile-testing" 
                element={<MobileResponsivenessDashboard />} 
              />
              
              {/* Resend API Test (Development/Testing) */}
              <Route 
                path="/resend-api-test" 
                element={<ResendApiTest />} 
              />
              
              {/* Company Dashboard Hub with nested routes */}
              <Route path="/company-dashboard-hub/:companyId/*" element={<CompanyDashboardHub />} />
              
              {/* Subscription Management Hub */}
              <Route path="/subscription-management-hub/:companyId" element={<SubscriptionManagementHub />} />
              
              {/* Candidate Unlock System */}
              <Route path="/candidate-unlock-system/:companyId" element={<CandidateUnlockSystem />} />
              
              {/* Analytics Dashboard - Company Admin & Hiring Manager Only */}
              <Route path="/analytics-dashboard" element={<RoleProtectedRoute allowedRoles={['company_admin', 'hiring_manager']}><AnalyticsDashboard /></RoleProtectedRoute>} />
              
              {/* Error Monitoring Dashboard - Company Admin & Hiring Manager Only */}
              <Route path="/error-monitoring-dashboard" element={<RoleProtectedRoute allowedRoles={['company_admin', 'hiring_manager']}><ErrorMonitoringDashboard /></RoleProtectedRoute>} />
              
              {/* SEO Validation Dashboard - Company Admin & Hiring Manager Only */}
              <Route path="/seo-validation-dashboard" element={<RoleProtectedRoute allowedRoles={['company_admin', 'hiring_manager']}><SEOValidationDashboard /></RoleProtectedRoute>} />
              
              {/* Catch-all route */}
              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
          </Suspense>
        </AnalyticsWrapper>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;